import { DESCRIBE_LAYER } from '../domain/constants';
import { Messaging } from 'oskari-ui/util';
/**
 * @class map.layer.handler
 * Handles requests concerning map layers.
 */
Oskari.clazz.define('map.layer.handler',
/**
 * @method create called automatically on construction
 * @static
 *
 * @param {Oskari.mapframework.domain.Map}
 *            mapState reference to state object
 */

function (mapState, layerService, getMsg) {
  this.mapState = mapState;
  this.getMsg = getMsg;
  this.layerService = layerService;
  this.log = Oskari.log('map.layer.handler');
  this.layerQueue = [];
}, {
  /**
   * @method handleRequest
   * Handles the request
   * @param {Oskari.mapframework.core.Core} core
   *      reference to the application core (reference sandbox core.getSandbox())
   * @param {Oskari.mapframework.request.Request} request
   *      request to handle
   */
  handleRequest: function handleRequest(core, request) {
    var sandbox = this.layerService.getSandbox();
    if (request.getName() === 'activate.map.layer') {
      var layerId = request.getLayerId();
      if (request.isActivated()) {
        this.mapState.activateLayer(layerId, request._creator);
      } else {
        this.mapState.deactivateLayer(layerId, request._creator);
      }
    } else if (request.getName() === 'AddMapLayerRequest') {
      this._addToMap(request.getMapLayerId(), request.getOptions(), request._creator);
    } else if (request.getName() === 'RemoveMapLayerRequest') {
      this.mapState.removeLayer(request.getMapLayerId(), request._creator);
    } else if (request.getName() === 'RearrangeSelectedMapLayerRequest') {
      this.mapState.moveLayer(request.getMapLayerId(), request.getToPosition(), request._creator);
    } else if (request.getName() === 'ChangeMapLayerOpacityRequest') {
      var layer = this.mapState.getSelectedLayer(request.getMapLayerId());
      var opacity = request.getOpacity();
      if (!layer || isNaN(opacity)) {
        return;
      }
      layer.setOpacity(Number(opacity));
      var evt = Oskari.eventBuilder('AfterChangeMapLayerOpacityEvent')(layer);
      evt._creator = request._creator;
      sandbox.notifyAll(evt);
    } else if (request.getName() === 'ChangeMapLayerStyleRequest') {
      var _layer = this.mapState.getSelectedLayer(request.getMapLayerId());
      if (!_layer) {
        return;
      }
      _layer.selectStyle(request.getStyle());
      var _evt = Oskari.eventBuilder('AfterChangeMapLayerStyleEvent')(_layer);
      _evt._creator = request._creator;
      sandbox.notifyAll(_evt);
    }
  },
  /**
   * Triggers loading additional info for layers (when needed) and adding them to a queue to be added to map.
   * Adding can be asynchronous for some layers, but the order the method is called will be respected
   * @param {String} layerId id for layer to add
   * @param {String} triggeredBy (optional) what triggered the add
   */
  _addToMap: function _addToMap(layerId) {
    var _this = this;
    var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var triggeredBy = arguments.length > 2 ? arguments[2] : undefined;
    var layer = this.layerService.findMapLayer(layerId);
    var layerStatus = {
      layer: layer,
      triggeredBy: triggeredBy,
      ready: false
    };
    // use queue so the layers are added to map in the same order as they are added with the request
    // otherwise layers would be added to map in the order where the additional metadata loading was completed
    this.layerQueue.push(layerStatus);
    var done = function done(error) {
      // change status for this layer
      if (error) {
        // filter layerStatus out from queue so it doesn't block other layers from being added to the map
        _this.layerQueue = _this.layerQueue.filter(function (status) {
          return status !== layerStatus;
        });
        Messaging.error(_this.getMsg('layerUnsupported.unavailable', {
          name: layer.getName()
        }));
      } else {
        layerStatus.ready = true;
      }
      // add layers from front of queue to map
      _this.__processLayerQueue();
      // zoom to content or center/supported zoom level
      var sandbox = _this.layerService.getSandbox();
      if (opts.zoomContent) {
        sandbox.postRequestByName('MapModulePlugin.MapMoveByLayerContentRequest', [layerId, opts.zoomContent]);
      }
      if (typeof opts.toPosition === 'number') {
        sandbox.postRequestByName('RearrangeSelectedMapLayerRequest', [layerId, opts.toPosition]);
      }
    };
    this._loadLayerInfo(layer, opts, done);
  },
  /**
   * Processes the queue for layers to add to map in the order they were requested to be added.
   * The queue is used to ensure order even when theres some asynchronous loading happening between
   * request and actual adding to the map
   */
  __processLayerQueue: function __processLayerQueue() {
    var nextLayer = this.layerQueue[0];
    while (nextLayer && nextLayer.ready) {
      this.mapState.addLayer(nextLayer.layer, nextLayer.triggeredBy);
      this.layerQueue.shift();
      nextLayer = this.layerQueue[0];
    }
  },
  _loadLayerInfo: function _loadLayerInfo(layer, opts, done) {
    var _this2 = this;
    if (typeof layer.getDescribeLayerStatus !== 'function') {
      // layer type doesn't support this
      done();
      return;
    }
    var layerId = layer.getId();
    var status = layer.getDescribeLayerStatus();
    if (status === DESCRIBE_LAYER.PREDEFINED) {
      this.__handleLayerInfoSuccess(layer, layer.getDescribeLayerInfo());
      done();
      return;
    }
    if (status === DESCRIBE_LAYER.LOADED || isNaN(layerId)) {
      // only layers that have numeric ids can have reasonable response for DescribeLayer
      // already processed, we can proceed with adding the layer to map
      done();
      return;
    }
    if (status === DESCRIBE_LAYER.PENDING) {
      return;
    }
    layer.setDescribeLayerStatus(DESCRIBE_LAYER.PENDING);
    this.layerService.getDescribeLayer(layer, opts, function (info) {
      if (!info) {
        layer.setDescribeLayerStatus(DESCRIBE_LAYER.ERROR);
        if (layer.requiresDescribeLayer()) {
          _this2.log.error('Attempt to add layer that requires more info. Skipping id: ' + layerId);
          done(true);
          return;
        }
      }
      _this2.__handleLayerInfoSuccess(layer, info);
      done();
    });
  },
  __handleLayerInfoSuccess: function __handleLayerInfoSuccess(layer) {
    var describeInfo = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var sandbox = this.layerService.getSandbox();
    var mapModule = sandbox.findRegisteredModuleInstance('MainMapModule');
    layer.setDescribeLayerStatus(DESCRIBE_LAYER.LOADED);
    layer.handleDescribeLayer(describeInfo);
    mapModule.handleDescribeLayer(layer, describeInfo);
    var event = Oskari.eventBuilder('MapLayerEvent')(layer.getId(), 'update');
    sandbox.notifyAll(event);
  }
}, {
  /**
   * @property {String[]} protocol array of superclasses as {String}
   * @static
   */
  protocol: ['Oskari.mapframework.core.RequestHandler']
});